import { Button, Classes, Dialog, Divider, FormGroup, DialogProps, Intent } from '@blueprintjs/core'
import { DateRange, DateRangeInput } from '@blueprintjs/datetime'
import * as React from 'react'
import client from '../../client'
import ReportHistory from '../../routes/reports/components/ReportHistory'
import { ReportType } from '../../routes/reports/types'
import { showSuccess } from '../../Toast'
import { UPRIGHT_EPOCH } from '../../utils'
import { buildDateOnlyISOString } from '../../utils/date'
import { dateRangeDayCounter } from '../../utils/dateRange'
import { toStartOfDay, toEndOfDay, diffIn } from '../../utils/dateHelpers'
import EmailSelector from '../EmailSelector'
import Errors from '../Errors'

interface ProductExporterDialogProps extends DialogProps {
  isOpen: boolean
  reportType: ReportType
}

interface ReportGeneratorAPIPayload {
  emails: string[]
  parameters: any
  reportType: ReportType
}

const ProductExporterDialog: React.FC<ProductExporterDialogProps> = props => {
  const [emails, setEmails] = React.useState([])
  const [errors, setErrors] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const timeStart = toStartOfDay(new Date())
  const timeEnd = toEndOfDay(new Date())

  const [reportDateRange, setReportDateRange] = React.useState<DateRange | null>([timeStart, timeEnd])
  const isDateRangeSet = reportDateRange.filter(date => date).length === 2
  const maxDayCount = 180

  const onEmailChange = values => {
    setEmails(values)
    setErrors(null)
  }

  const cleanupAndClose = e => {
    setEmails([])
    setErrors(null)
    setReportDateRange([timeStart, timeEnd])
    props.onClose(e)
  }

  const onSubmit = async () => {
    setLoading(true)
    if (reportDateRange.filter(date => date).length !== 2) {
      setErrors({ errors: { dates: ['both a start and end date must be specified'] } })
      setLoading(false)
      return
    }
    const length = diffIn(reportDateRange[1], reportDateRange[0], 'days')
    if (length > maxDayCount) {
      setErrors({ errors: { dates: [`cannot span more than ${maxDayCount} days`] } })
      setLoading(false)
      return
    }

    const timeParams = {
      ignoreRecycledFilter: true,
      timeFrameDimension: 'created_at',
      timeStart: buildDateOnlyISOString(reportDateRange[0]),
      timeEnd: buildDateOnlyISOString(reportDateRange[1])
    }

    const payload: ReportGeneratorAPIPayload = {
      emails,
      parameters: timeParams,
      reportType: props.reportType
    }

    try {
      await client.generateReport({ asyncReport: payload })
      setErrors(null)
      showSuccess('Report generated')
    } catch (e) {
      setErrors(e.response.data)
    }

    analytics.track(`Products Page Data Export`, {
      startDate: reportDateRange[0],
      endDate: reportDateRange[1],
      dayCount: dateRangeDayCounter(reportDateRange[0], reportDateRange[1]),
      emailCount: emails.length
    })
    setLoading(false)
  }

  return (
    <Dialog
      {...props}
      onClose={e => cleanupAndClose(e)}
      title="Export Products"
      portalClassName="upright-popover-safe-dialog"
    >
      <div className={Classes.DIALOG_BODY}>
        <Errors error={errors} />

        <FormGroup label="Export Date Range">
          <DateRangeInput
            allowSingleDayRange
            shortcuts={false}
            maxDate={new Date()}
            minDate={UPRIGHT_EPOCH}
            formatDate={date => date.toLocaleDateString()}
            onChange={setReportDateRange}
            popoverProps={{ usePortal: false }}
            parseDate={str => new Date(str)}
            value={reportDateRange}
          />
        </FormGroup>

        <FormGroup
          helperText="We will email you when the report is ready."
          label="Email addresses"
          labelInfo="(optional)"
          style={{ marginBottom: 10 }}
        >
          <EmailSelector onChange={(values: string[]) => onEmailChange(values)} values={emails} />
        </FormGroup>

        <ReportHistory reportType={props.reportType} reportsCount={5} />
      </div>

      <Divider style={{ margin: '0px 0 10px' }} />

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={cleanupAndClose}>Cancel</Button>
          <Button intent={Intent.PRIMARY} onClick={onSubmit} loading={loading} disabled={!isDateRangeSet}>
            Generate report
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default ProductExporterDialog
